<script setup>
import { ref } from 'vue';
import { useAlert } from 'dashboard/composables';
import CRMDealsAPI from 'dashboard/api/crm/deals.js';
import DealMoveForm from 'dashboard/components/crm/DealMoveForm.vue';

const emit = defineEmits(['moved', 'form-required', 'cancelled', 'move-failed']);

const showForm = ref(false);
const isLoading = ref(false);
const moveContext = ref(null);

// Orquesta el flujo completo de cambio de etapa (server-authoritative): intenta
// mover sin form_data, y si el stage destino lo requiere, muestra DealMoveForm
// antes de confirmar. Reusable desde cualquier superficie (drag, modal, sidebar).
const startMove = async ({ deal, targetStageId }) => {
  if (!deal?.id || !targetStageId) return;
  if (Number(deal.stage_id) === Number(targetStageId)) return;

  try {
    const { data } = await CRMDealsAPI.move({
      id: deal.id,
      stageId: targetStageId,
      formData: null,
    });

    if (data.requires_form) {
      moveContext.value = {
        dealId: deal.id,
        targetStageId,
        deal: data.deal,
        targetStage: data.target_stage,
        formConfig: data.form_config,
        stageType: data.stage_type,
      };
      showForm.value = true;
      emit('form-required', { deal: data.deal, targetStage: data.target_stage });
      return;
    }

    emit('moved', data);
  } catch (error) {
    useAlert('Error al mover el negocio de etapa.');
    // Falló antes de saber si requería formulario: quien orquesta el drag (si
    // aplica) necesita revertir su UI optimista sabiendo el targetStageId que
    // se intentó, porque el servidor nunca llegó a confirmarlo ni rechazarlo.
    emit('move-failed', { deal, targetStageId });
  }
};

// payload llega como { formData, dealUpdates } (ver DealMoveForm#handleSubmit) y
// se reenvía tal cual: MoveStageService espera esa forma para separar los campos
// de auditoría de los que actualizan atributos del deal.
const handleSubmit = async payload => {
  if (!moveContext.value) return;

  isLoading.value = true;
  try {
    const { data } = await CRMDealsAPI.move({
      id: moveContext.value.dealId,
      stageId: moveContext.value.targetStageId,
      formData: payload,
    });
    showForm.value = false;
    moveContext.value = null;
    emit('moved', data);
  } catch (error) {
    useAlert('Error al mover el negocio de etapa.');
  } finally {
    isLoading.value = false;
  }
};

const handleCancel = () => {
  showForm.value = false;
  moveContext.value = null;
  emit('cancelled');
};

defineExpose({ startMove });
</script>

<template>
  <DealMoveForm
    v-if="showForm && moveContext"
    :deal="moveContext.deal"
    :target-stage="moveContext.targetStage"
    :form-config="moveContext.formConfig"
    :stage-type="moveContext.stageType"
    :loading="isLoading"
    @submit="handleSubmit"
    @cancel="handleCancel"
  />
</template>
